import { Button, ColumnTable } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { TranslationsKeys } from '../../../../../locales/translations';
import { StudentsRemovalRequestHistory } from '../../types/history';
import BadgeState from '../../../../../components/badgeStatus/BadgeState';
import { PathsLayouts } from '../../../../../config/routes';

export function useColumns(): ColumnTable<StudentsRemovalRequestHistory>[] {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_HISTORY,
  );

  const history = useHistory();

  const goToView = useCallback(
    (id: number) => {
      history.push(`${PathsLayouts.studentsRemovalRequest}/${id}`);
    },
    [history],
  );

  const table = useMemo(() => {
    return t<
      string,
      {
        headers: {
          studentName: string;
          studentIdentification: string;
          studyPlanVersionName: string;
          requestedBy: string;
          requestedState: string;
          resolvedBy: string;
          action: string;
        };
        viewDetail: string;
      }
    >('history.table', {
      returnObjects: true,
    });
  }, [t]);

  return useMemo(
    () => [
      {
        columnName: 'sectionCode',
        headerText: table.headers.studentName,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            {row.student.accountLastNames}, {row.student.accountNames}
          </>
        ),
      },
      {
        columnName: 'student',
        headerText: table.headers.studentIdentification,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            {row.student.identificationTypeName}{' '}
            {row.student.identificationValueFormatted}
          </>
        ),
      },
      {
        columnName: 'studyPlanVersion',
        headerText: table.headers.studyPlanVersionName,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => <>{row.studyPlanVersion.name}</>,
      },
      {
        columnName: 'removalRequest',
        headerText: table.headers.requestedBy,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            <div>
              {row.removalRequest.requestedBy.accountLastNames},{' '}
              {row.removalRequest.requestedBy.accountNames}
            </div>
            <div>
              {dayjs(row.removalRequest.requestedAt).format('DD/MM/YYYY')}
            </div>
          </>
        ),
      },
      {
        columnName: 'requestState',
        headerText: table.headers.requestedState,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <BadgeState
            status={row.removalRequest.state}
            genderContext="female"
          />
        ),
      },
      {
        columnName: 'resolvedBy',
        headerText: table.headers.resolvedBy,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => {
          if (!row.removalRequest.response) {
            return null;
          }
          const response = row.removalRequest.response;
          return (
            <>
              <div>
                {response.resolvedBy.accountLastNames},{' '}
                {response.resolvedBy.accountNames}
              </div>
              <div>{dayjs(response.resolvedAt).format('DD/MM/YYYY')}</div>
            </>
          );
        },
      },
      {
        columnName: 'action',
        headerText: table.headers.action,
        tdClassName: 'text-center text-primary',
        thClassName: 'text-center',
        width: 120,
        cellFormat: ({ row }) => (
          <Button
            type="button"
            text={table.viewDetail}
            size="sm"
            color="primary"
            onClick={() => goToView(row.removalRequest.id)}
          />
        ),
      },
    ],
    [table, goToView],
  );
}
